import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Services: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const services = [
    { id: 'iot',         image: '/images/services/iot.png',    projects: ['TotalEnergies', 'Stedis', 'SeaUp','Teréga'] },
    { id: 'integration', image: '/images/services/api.png',    projects: ['TotalEnergies', 'Teréga', 'DatAsset'] },
    { id: 'mobile',      image: '/images/services/mobile.png', projects: ['Mekas'] },
    { id: 'web',         image: '/images/services/web.png',    projects: ['DatAsset', 'Mekas'] },
  ];

  const scrollToContact = () => {
    window.dispatchEvent(new CustomEvent('openContactChat'));
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      {/* Heading */}
      <div className="text-center mb-16">
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: 'var(--color-text-primary)',
          }}
        >
          {t('services.title')}
        </h1>
        <div
          className="mx-auto mt-4"
          style={{ width: 60, height: 3, background: 'var(--gradient-primary)', borderRadius: 2 }}
        />
      </div>

      {/* Services grid */}
      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {services.map((service) => {
          const isHovered = hoveredService === service.id;
          const isExpanded = expandedService === service.id;

          /* ── Mobile accordion ────────────────────── */
          if (isMobile) {
            return (
              <div key={service.id} className="card-glass" style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
                {/* Tap header */}
                <button
                  onClick={() => setExpandedService(isExpanded ? null : service.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                >
                  <div style={{ flexShrink: 0, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={service.image}
                      alt={t(`services.${service.id}.title`)}
                      style={{ filter: 'brightness(0) invert(1)', width: 36, height: 36, objectFit: 'contain' }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1.125rem', color: 'var(--color-text-primary)', margin: 0 }}>
                      {t(`services.${service.id}.title`)}
                    </h2>
                    <p style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '0.875rem', color: 'var(--color-primary)', marginTop: 2, marginBottom: 0 }}>
                      {t(`services.${service.id}.subtitle`)}
                    </p>
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'var(--color-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '1.25rem',
                      fontWeight: 300,
                      lineHeight: 1,
                      transition: 'transform 250ms',
                      transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                    }}
                  >
                    +
                  </span>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(54,109,198,0.15)' }}>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text-secondary)', padding: '12px 0' }}>
                      {t(`services.${service.id}.description`)}
                    </p>
                    <div style={{ background: 'var(--color-surface-1)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginTop: 8 }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.875rem', color: 'var(--color-text-primary)', marginTop: 0, marginBottom: 8 }}>
                        {t('services.relatedProjects')}
                      </h3>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {service.projects.map((p) => (
                          <li key={p} style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--color-primary)', paddingBottom: 4 }}>{p}</li>
                        ))}
                      </ul>
                    </div>
                    <button
                      onClick={() => navigate('/use-cases')}
                      className="btn-primary text-sm"
                      style={{ marginTop: 12, width: '100%', padding: '10px 16px' }}
                    >
                      {t('services.viewUseCases', "Voir les cas d'usage")}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          /* ── Desktop hover-slide ─────────────────── */
          return (
            <div
              key={service.id}
              className="relative card-glass flex"
              style={{ borderRadius: 'var(--radius-xl)', height: 260, overflow: 'hidden' }}
              onMouseEnter={() => setHoveredService(service.id)}
              onMouseLeave={() => setHoveredService(null)}
            >
              {/* Fixed icon column — never moves */}
              <div className="flex-shrink-0 p-4 flex items-center justify-center" style={{ width: '25%' }}>
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    background: 'transparent',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <img
                    src={service.image}
                    alt={t(`services.${service.id}.title`)}
                    style={{
                      filter: 'brightness(0) invert(1)',
                      width: '70%',
                      height: '70%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>

              {/* Sliding area — only text + projects slide */}
              <div className="flex-1 relative overflow-hidden">
                <div
                  className="flex h-full"
                  style={{
                    width: '200%',
                    transform: isHovered ? 'translateX(-50%)' : 'translateX(0)',
                    transition: 'transform 480ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {/* Text panel */}
                  <div className="p-6 flex flex-col justify-center overflow-hidden flex-shrink-0" style={{ width: '50%' }}>
                    <h2
                      className="mb-3"
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: '1.375rem',
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {t(`services.${service.id}.title`)}
                    </h2>
                    <p
                      className="mb-2"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontWeight: 500,
                        fontSize: '0.9375rem',
                        color: 'var(--color-primary)',
                      }}
                    >
                      {t(`services.${service.id}.subtitle`)}
                    </p>
                    <p
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        color: 'var(--color-text-secondary)',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {t(`services.${service.id}.description`)}
                    </p>
                  </div>

                  {/* Projects panel */}
                  <div
                    className="flex flex-col items-center justify-center h-full p-8 flex-shrink-0"
                    style={{
                      width: '50%',
                      background: 'var(--color-surface-1)',
                      borderLeft: '1px solid rgba(54,109,198,0.15)',
                    }}
                  >
                    <h3
                      className="mb-4"
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {t('services.relatedProjects')}
                    </h3>
                    <ul className="mb-5 space-y-1.5 text-center">
                      {service.projects.map((p) => (
                        <li
                          key={p}
                          style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.9375rem',
                            color: 'var(--color-primary)',
                          }}
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate('/use-cases')}
                      className="btn-primary text-sm px-5 py-2"
                    >
                      {t('services.viewUseCases', "Voir les cas d'usage")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Border glow on hover */}
              <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                style={{
                  borderRadius: 'var(--radius-xl)',
                  border: '1px solid rgba(54,109,198,0.35)',
                  opacity: isHovered ? 1 : 0,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* CTA Section */}
      <div
        className="relative mt-24 rounded-2xl overflow-hidden text-center py-16 px-8"
        style={{
          background: 'var(--color-surface-1)',
          border: '1px solid var(--color-border)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: 'var(--radius-2xl)',
        }}
      >
        {/* Glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '600px', height: '300px',
            background: 'radial-gradient(ellipse, rgba(54,109,198,0.12) 0%, transparent 70%)',
            filter: 'blur(30px)',
          }}
        />

        <div className="relative z-10">
          <h2
            className="mb-4"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '2rem', color: 'var(--color-text-primary)' }}
          >
            {t('services.cta.title')}
          </h2>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)', lineHeight: 1.7 }}
          >
            {t('services.cta.description')}
          </p>
          <button onClick={scrollToContact} className="btn-primary px-8 py-3">
            {t('services.cta.button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Services;
